// Erste-Person-Kamera. Nur lateUpdate — Spielerposition muss fertig sein.
//
// Konzept §12.1: leichtes Schwanken und Schienenstöße erzeugen das Fahrgefühl
// zuverlässiger als Grafik. Aber sparsam: zu viel macht seekrank, und §14
// nennt Motion Sickness ausdrücklich als Risiko.
//
// yaw/pitch gehören dem PlayerSystem — die Kamera liest sie, damit Figur und
// Blick nie auseinanderlaufen. `comfort` skaliert alle Amplituden global
// (VR-Komfortmodus später).

import * as THREE from 'three';
import { PLAYER_EYE_HEIGHT } from '../../shared/constants.js';

// Wiederverwendete Vektoren — nichts pro Frame allokieren.
const _look = new THREE.Vector3();
const _eye = new THREE.Vector3();

// Basis-Amplituden (Meter / Radiant). Klein halten.
const BOB_WALK = 0.012;
const BOB_RUN = 0.022;
const SWAY_RIDE = 0.006;       // ruhige Nickbewegung auf dem Waggon
const JOLT_PITCH = 0.012;
const FALL_DIP = 0.08;
const DOWN_TILT = 0.35;
const SHIVER_AMP = 0.004;

export class CameraSystem {
  async init(ctx) {
    // comfort: 0 = starr, 1 = voll. Später UI / VR-Modus.
    this.comfort = 1;
    this._bobPhase = 0;
    this._swayPhase = 0;
    this._shiverT = 0;
    this._fallBlend = 0;
    this._downBlend = 0;
    this._joltSmooth = 0;
    this._eyeSmooth = PLAYER_EYE_HEIGHT;
    this._pitchOff = 0;
    this._rollOff = 0;
    this._yawOff = 0;

    // Kamera initial an Spawn
    if (ctx.playerState) {
      this._apply(ctx, 0);
    }
  }

  update() {
    // Absichtlich leer — nur lateUpdate.
  }

  lateUpdate(dt, ctx) {
    const st = ctx.playerState;
    if (!st) return;

    // Player kann comfort überschreiben (z. B. Debug / späteres Menü)
    if (typeof st.comfort === 'number') this.comfort = st.comfort;
    const c = this.comfort;

    // Kopfhöhe weich (Player liefert schon interpoliert, wir glätten nochmal leicht)
    const targetEye = st.eyeHeight ?? PLAYER_EYE_HEIGHT;
    this._eyeSmooth = THREE.MathUtils.lerp(this._eyeSmooth, targetEye, 1 - Math.exp(-14 * dt));

    // --- Schritt-Rhythmus beim Gehen/Rennen --------------------------------
    const grounded = st.grounded && (st.mode === 'ground' || st.mode === 'ride' || st.mode === 'inside');
    let moveSp = 0;
    if (grounded) {
      // Weltgeschwindigkeit abzüglich Zuganteil grob: auf dem Boden volle v
      moveSp = Math.hypot(st.velocity.x, st.velocity.z);
      if (st.onCarId && ctx.train) {
        // Auf dem Zug: Bob nur wenn man selbst läuft (Anim walk/run)
        const a = st.anim;
        moveSp = (a === 1 || a === 2) ? 2.5 : (a === 3 ? 1.0 : 0);
      }
    }
    const running = moveSp > 4.0 || st.anim === 2;
    if (moveSp > 0.4 && grounded) {
      const freq = running ? 9.5 : 6.5;
      this._bobPhase += dt * freq * Math.min(1.4, moveSp / 2.5);
    } else {
      // auspendeln
      this._bobPhase *= Math.exp(-4 * dt);
    }
    const bobAmp = (running ? BOB_RUN : BOB_WALK) * c;
    const bobY = Math.sin(this._bobPhase * 2) * bobAmp;
    const bobX = Math.sin(this._bobPhase) * bobAmp * 0.45;

    // --- Fahr-Schwanken + Schienenstöße ------------------------------------
    // Frequenz an realSpeed koppeln; Amplitude klein.
    const realSpeed = ctx.train?.realSpeed ?? 0;
    const onCar = !!(st.onCarId && (st.mode === 'ride' || st.mode === 'inside' || st.mode === 'hang'));
    if (onCar && realSpeed > 0.5) {
      this._swayPhase += dt * (0.7 + realSpeed * 0.06);
    }
    const sway = onCar
      ? Math.sin(this._swayPhase) * SWAY_RIDE * c * Math.min(1, realSpeed / 12)
      : 0;

    const jolt = ctx.train?.jolt ?? 0;
    // Glätten, damit einzelne Spikes nicht schlagen
    this._joltSmooth = THREE.MathUtils.lerp(this._joltSmooth, jolt, 1 - Math.exp(-10 * dt));
    const joltKick = this._joltSmooth * JOLT_PITCH * c * (st.crouching ? 0.25 : 1);

    // --- Fallen / Hinfallen ------------------------------------------------
    const falling = st.mode === 'air' && st.velocity.y < -2;
    this._fallBlend = THREE.MathUtils.lerp(this._fallBlend, falling ? 1 : 0, 1 - Math.exp(-6 * dt));
    const fallDip = this._fallBlend * FALL_DIP * c;

    const down = st.mode === 'down' ? 1 : 0;
    this._downBlend = THREE.MathUtils.lerp(this._downBlend, down, 1 - Math.exp(-5 * dt));
    const downTilt = this._downBlend * DOWN_TILT * c;

    // --- Kältezittern ------------------------------------------------------
    let shiver = 0;
    if (st.warmth < 28) {
      this._shiverT += dt;
      const intensity = (1 - st.warmth / 28);
      // unregelmäßig: zwei versetzte Sinus
      shiver = (
        Math.sin(this._shiverT * 17.3) * 0.55 +
        Math.sin(this._shiverT * 29.1 + 1.7) * 0.45
      ) * SHIVER_AMP * intensity * c;
    }

    // Offsets zusammenfassen
    this._pitchOff = sway + joltKick * Math.sin(this._swayPhase * 3.1) - fallDip * 0.4 - downTilt * 0.5;
    this._rollOff = bobX * 0.8 + shiver * 8 + joltKick * 0.35 * Math.sin(this._swayPhase * 2.2 + 1.0);
    this._yawOff = shiver * 5;

    this._apply(ctx, bobY + shiver - fallDip * 0.5 - this._downBlend * 0.15);
  }

  _apply(ctx, yOff) {
    const cam = ctx.camera;
    const st = ctx.playerState;
    if (!cam || !st) return;

    const eyeH = this._eyeSmooth + yOff;
    _eye.set(st.pos.x, st.pos.y + eyeH, st.pos.z);
    cam.position.copy(_eye);

    // Blickrichtung aus yaw/pitch des Spielers + kleinen Komfort-Offsets
    const yaw = st.yaw + this._yawOff;
    const pitch = st.pitch + this._pitchOff;
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    const sy = Math.sin(yaw), cy = Math.cos(yaw);
    // yaw 0 → -Z
    _look.set(sy * cp, sp, -cy * cp);
    cam.up.set(
      Math.sin(this._rollOff) * 0.15,
      1,
      0,
    );
    // up leicht rollen ohne Quaternion-Allokation
    cam.up.set(Math.sin(this._rollOff), Math.cos(this._rollOff), 0).normalize();
    cam.lookAt(_eye.x + _look.x, _eye.y + _look.y, _eye.z + _look.z);
  }

  dispose() {}
}
